import { CanineHeader, CanineFooter } from "../components/CanineShell";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import "./BoerboelsHome.css";
import { DogCard } from "../components/DogCard";
import api from "../lib/api";

const GROUPS = [
  { key: "available", label: "Available Puppies" },
  { key: "coming_soon", label: "Upcoming Litters" },
  { key: "reserved", label: "Reserved" },
];

export default function Boerboels() {
  const [dogs, setDogs] = useState([]);
  useEffect(() => { api.get("/dogs?eon_owned=true").then((r) => setDogs(r.data)); }, []);
  const byStatus = (s) => dogs.filter((d) => d.status === s);

  return (
    <div className="canine-home"><a className="canine-skip" href="#canine-main">Skip to content</a><CanineHeader /><main id="canine-main">
      <section className="canine-hero">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-24 w-full">
          <div className="canine-kicker">THE BOERBOELS BUSINESS</div>
          <h1 className="canine-title">EON Boerboels</h1>
          <p className="canine-lead">
            Explore Boerboel dogs and puppies, owner education, breeding resources and the developing E.O.N. Nutrition collection.
          </p>
        <div className="canine-actions"><a className="canine-button" href="#canine-listings">Explore dogs &amp; litters</a><Link className="canine-outline" to="/boerboels/academy">Books &amp; owner education</Link></div></motion.div><figure className="canine-hero-image"><img src="/corporate-approved/boerboels.jpg" alt="Boerboel breed illustration"/><figcaption>Breed illustration. View individual listings for specific dogs.</figcaption></figure>
      </section>

      <div className="canine-wrap canine-content"><section id="canine-listings" className="canine-intro"><p className="canine-kicker">DOGS &amp; LITTERS</p><h2>Find your next step.</h2><p>Explore current listings, learn about the breed, or contact E.O.N. with a puppy inquiry.</p><div className="canine-actions"><Link className="canine-button" to="/dogs">View dog listings</Link><Link className="canine-outline" to="/contact">Ask about EON Boerboels</Link></div></section>
        {GROUPS.map((g) => byStatus(g.key).length > 0 && (
          <div key={g.key} className="mb-16">
            <div className="flex items-end justify-between mb-8">
              <h2 className="eon-heading text-3xl sm:text-4xl uppercase">{g.label}</h2>
              <span className="eon-label">{byStatus(g.key).length} dogs</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {byStatus(g.key).map((d, i) => <DogCard key={d.id} dog={d} index={i} />)}
            </div>
          </div>
        ))}

        <div id="canine-standard" className="border border-eon-border bg-eon-surface p-6 lg:p-14 mb-16">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <div className="eon-label mb-4">The E.O.N Standard</div>
              <h3 className="eon-heading text-3xl uppercase">What every E.O.N puppy comes with</h3>
              <p className="mt-4 text-eon-text/80 leading-relaxed">
                Our Boerboels are a South African breed, <span className="text-eon-gold">microchipped and DNA tested</span>. Every puppy leaves at 8 weeks old, fully documented and health-guaranteed.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["SABBS", "NABBR", "AKC"].map((r) => (
                  <span key={r} className="eon-label border border-eon-gold/40 text-eon-gold px-3 py-1.5">Registered · {r}</span>
                ))}
              </div>
            </div>
            <ul className="grid sm:grid-cols-2 gap-3" data-testid="eon-puppy-package">
              {[
                "NABBA puppy registration", "Health Certificate", "Up to date on shots with health record",
                "Microchipped", "DNA tested", "Genetic health guarantee (hips, heart, etc.)",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 eon-card p-4 text-sm text-eon-text/90">
                  <CheckCircle2 size={18} className="text-eon-gold shrink-0 mt-0.5" /> {item}
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-8 pt-6 border-t border-eon-border text-sm text-eon-muted">
            Puppies leave when they are <span className="text-eon-text">8 weeks old</span>. All E.O.N dogs are registered with <span className="text-eon-text">SABBS, NABBR and AKC</span>.
          </p>
        </div>

        <div className="border border-eon-border bg-eon-surface p-6 lg:p-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eon-label mb-4">Reservation Process</div>
            <h3 className="eon-heading text-3xl uppercase">How to reserve an E.O.N puppy</h3>
            <ol className="mt-6 space-y-4 text-eon-text/80">
              {["Submit a puppy application on any listing", "Our team reviews fit and reaches out", "Place a deposit to secure your reservation window", "Select your puppy and finalize placement"].map((s, i) => (
                <li key={s} className="flex gap-4"><span className="font-mono text-eon-gold">0{i + 1}</span><span>{s}</span></li>
              ))}
            </ol>
          </div>
          <div className="space-y-4">
            <Link to="/breeds/boerboel" className="eon-card p-6 block"><div className="eon-label mb-1">Learn the Breed</div><div className="font-display text-xl font-bold">Boerboel Breed Guide →</div></Link>
            <Link to="/boerboels/academy" className="eon-card p-6 block"><div className="eon-label mb-1">Owner Education</div><div className="font-display text-xl font-bold">Books & Owner Education →</div></Link>
            <Link to="/dogs" className="eon-card p-6 block"><div className="eon-label mb-1">Explore More</div><div className="font-display text-xl font-bold">Full Marketplace →</div></Link>
          </div>
        </div>
      </div>
<section id="canine-resources" className="canine-resources"><div className="canine-wrap"><p className="canine-kicker">LEARN. CONNECT. GROW.</p><h2>Resources for the Boerboel community.</h2><div className="canine-resource-grid">{[ ["Nutrition · Coming Soon","Explore existing food and supplement concepts. Not yet available to buy.","/shop"], ["Breeding & owner programs","Browse existing stud service, training and canine programs.","/boerboels/programs"], ["Partners","Explore canine industry partnerships.","/partners"], ["Membership","View existing membership options and benefits.","/membership"], ["Breed guide","Get to know the Boerboel.","/breeds/boerboel"], ["Books & education","Explore existing books, courses and owner resources.","/boerboels/academy"], ["Services","Browse the existing service directory.","/services"], ["Breeders","Explore breeder information and listings.","/breeders"], ["Community","Connect through the existing community area.","/community"], ["Help & inquiries","Contact E.O.N. about a dog, resource or purchase.","/contact"] ].map(([title,description,to])=><Link className="canine-resource-card" key={to} to={to}><h3>{title} <span aria-hidden="true">→</span></h3><p>{description}</p></Link>)}</div></div></section></main><CanineFooter /></div>
  );
}
